/**
 * Alert Inbox service
 * -------------------
 * One feed for everything the DB side raises for dispatch:
 *   - geofence_events with processed = false (the v2 engine: dwell,
 *     completion_pending, group_open, drive_by, gps_gap; break_* from the
 *     break trigger;
 *     eta_variance etc.)
 *   - public.notifications for the signed-in user (written by the
 *     analyze_load_change / notify_driver_report / notify_eta_variance
 *     triggers; nothing on the board read them before - audit S8).
 *
 * Both are read-and-acknowledge: dismissing sets processed / is_read.
 * NOTE geofence_events needs an UPDATE policy for owner/dispatcher
 * (sql/alert_inbox.sql) - without it the dismiss silently touches 0 rows.
 */

import { supabase } from '../supabaseClient';
import { parseDbTime } from '../utils/dbTime';

/** Event types worth a dispatcher's attention (enter/exit are noise on their own). */
export const ACTIONABLE_EVENT_TYPES = [
  'dwell', 'completion_pending', 'group_open', 'drive_by', 'gps_gap',
  'break_movement', 'break_movement_ended', 'break_auto_ended', 'break_long',
  'eta_variance', 'late_arrival', 'geofence_error',
  // __STATUS_TRUTH__ (2026-10-28) the hub's sweep: a job reported complete (Trello / the driver) that hauling cannot
  // complete yet - the row's text names what is missing (cargo type, time lost, proof of delivery)
  'completion_blocked',
];

export const fetchInbox = async ({ limit = 100 } = {}) => {
  const out = { geofence: [], notifications: [], error: null };
  try {
    const [{ data: ev, error: e1 }, { data: nt, error: e2 }] = await Promise.all([
      supabase
        .from('geofence_events')
        .select('id, event_type, stop_type, address, distance_meters, dwell_seconds, created_at, load_id, load_stop_id, driver_id, driver:drivers(id, phone, user:user_id(name)), load:loads(id, load_number, status)')
        .eq('processed', false)
        .in('event_type', ACTIONABLE_EVENT_TYPES)
        .order('created_at', { ascending: false })
        .limit(limit),
      supabase
        .from('notifications')
        .select('id, title, message, notification_type, priority, entity_type, entity_id, is_read, created_at')
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(limit),
    ]);
    if (e1) out.error = e1.message; else out.geofence = ev || [];
    if (e2) out.error = (out.error ? out.error + '; ' : '') + e2.message; else out.notifications = nt || [];
  } catch (e) {
    out.error = e.message;
  }
  return out;
};

export const countInbox = async () => {
  try {
    const [{ count: c1 }, { count: c2 }] = await Promise.all([
      supabase.from('geofence_events').select('id', { count: 'exact', head: true })
        .eq('processed', false).in('event_type', ACTIONABLE_EVENT_TYPES),
      supabase.from('notifications').select('id', { count: 'exact', head: true }).eq('is_read', false),
    ]);
    return (c1 || 0) + (c2 || 0);
  } catch (e) {
    return 0;
  }
};

export const dismissGeofenceEvent = async (id) => {
  const { data, error } = await supabase.from('geofence_events').update({ processed: true }).eq('id', id).select('id');
  if (error) return { ok: false, error: error.message };
  if (!data || data.length === 0) return { ok: false, error: 'No permission to acknowledge geofence events (missing UPDATE policy - run sql/alert_inbox.sql).' };
  return { ok: true };
};

export const dismissGeofenceEventsForLoad = async (loadId) => {
  const { error } = await supabase.from('geofence_events').update({ processed: true }).eq('load_id', loadId).eq('processed', false);
  return { ok: !error, error: error ? error.message : null };
};

export const markNotificationRead = async (id) => {
  const { error } = await supabase.from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() }).eq('id', id);
  return { ok: !error, error: error ? error.message : null };
};

export const markAllNotificationsRead = async () => {
  const { error } = await supabase.from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() }).eq('is_read', false);
  return { ok: !error, error: error ? error.message : null };
};

/** Realtime: fires on any new geofence event or notification (RLS-filtered by Supabase). */
export const subscribeInbox = (onChange) => {
  const ch = supabase
    .channel('alert_inbox')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'geofence_events' }, (p) => onChange('geofence', p.new))
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, (p) => onChange('notification', p.new))
    .subscribe();
  return () => supabase.removeChannel(ch);
};

const mins = (sec) => Math.round((sec || 0) / 60);

/** __STATUS_TRUTH__ the hub's blocked-completion text ("Job BK-... cannot be completed: ..."). */
export const isBlockedText = (t) => /^Job \S+ cannot be completed:/.test(String(t || ''));

/** Display model for a geofence event row (with or without the joins). */
export const describeGeofenceEvent = (ev) => {
  const driver = (ev.driver && ev.driver.user && ev.driver.user.name) || 'Driver';
  const loadNum = (ev.load && ev.load.load_number) || (ev.load_id ? String(ev.load_id).slice(0, 8) : '?');
  const stop = ev.stop_type ? ev.stop_type : 'stop';
  const addr = ev.address ? ev.address.split(',')[0] : '';
  const base = { priority: 'info', suggest: null };
  // __STATUS_TRUTH__ a blocked completion (also when the hub had to file it as completion_pending)
  if (ev.event_type === 'completion_blocked' || (ev.event_type === 'completion_pending' && isBlockedText(ev.address))) {
    return { ...base, icon: '⛔', priority: 'action', title: `Load #${loadNum} cannot be completed`,
      detail: ev.address || 'Something hauling requires is missing.' };
  }
  switch (ev.event_type) {
    case 'completion_pending':
      return { ...base, icon: '✅', priority: 'action', title: `${driver} left the delivery - Load #${loadNum}`,
        detail: `Confirm the job is complete (${mins(ev.dwell_seconds)} min on site). ${addr}`, suggest: { from: 'pending_completion', to: 'complete', label: 'Confirm complete' } };
    case 'group_open':
      return { ...base, icon: '📦', priority: 'action',
        title: `${driver} left ${addr || 'the yard'} - ${stop} not confirmed - Load #${loadNum}`,
        detail: `Another stop on this load is at the same place, so driving away does not prove this one is done (${mins(ev.dwell_seconds)} min on site). The driver confirms it in the app.` };
    case 'dwell':
      return { ...base, icon: '⏱', priority: 'action', title: `${driver} ${mins(ev.dwell_seconds)}+ min at ${stop} - Load #${loadNum}`,
        detail: addr };
    case 'drive_by':
      return { ...base, icon: '↩', priority: 'warn', title: `${driver} passed the ${stop} without stopping - Load #${loadNum}`,
        detail: `Status reverted. ${addr}` };
    case 'gps_gap':
      return { ...base, icon: '📵', priority: 'warn', title: `${driver}: GPS gap ${mins(ev.dwell_seconds)} min`,
        detail: ev.address || 'Phone offline or app closed' };
    case 'break_movement':
    case 'break_movement_ended':
    case 'break_auto_ended':
    case 'break_long':
      return { ...base, icon: '☕', priority: 'warn', title: `${driver}: ${ev.event_type.replace(/_/g, ' ')}`, detail: ev.address || '' };
    case 'eta_variance':
    case 'late_arrival':
      return { ...base, icon: '⏰', priority: 'warn', title: `${driver} running late - Load #${loadNum}`, detail: ev.address || '' };
    default:
      return { ...base, icon: '📌', title: `${driver}: ${ev.event_type} - Load #${loadNum}`, detail: ev.address || '' };
  }
};

export const ageText = (ts) => {
  const d = parseDbTime(ts);
  if (!d) return '';
  const m = Math.round((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (m < 60 * 24) return `${Math.round(m / 60)}h ago`;
  return d.toLocaleDateString();
};

export default { fetchInbox, countInbox, dismissGeofenceEvent, dismissGeofenceEventsForLoad, markNotificationRead, markAllNotificationsRead, subscribeInbox, describeGeofenceEvent, ageText };
